/**
 * Snowfall Toggle Context
 *
 * Provides a way for users to toggle the snowfall effect on/off.
 * State is persisted to localStorage so the preference survives page reloads.
 *
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import type { SnowfallContextValue, SnowfallProviderProps } from './types';

const STORAGE_KEY = 'neo4j-backstage-snowfall-enabled';

const SnowfallContext = createContext<SnowfallContextValue | null>(null);

/**
 * Provider component for snowfall toggle state.
 * Wrap your app or a section to enable the toggle functionality.
 *
 * @example
 * ```tsx
 * <SnowfallProvider>
 *   <App />
 * </SnowfallProvider>
 * ```
 */
export function SnowfallProvider({
  children,
  defaultEnabled = true,
}: SnowfallProviderProps) {
  const [enabled, setEnabledState] = useState<boolean>(() => {
    // Try to read from localStorage on initial render
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored !== null) {
        return stored === 'true';
      }
    }
    return defaultEnabled;
  });

  // Persist to localStorage when enabled changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, String(enabled));
    }
  }, [enabled]);

  const setEnabled = useCallback((value: boolean) => {
    setEnabledState(value);
  }, []);

  const toggle = useCallback(() => {
    setEnabledState(prev => !prev);
  }, []);

  const value: SnowfallContextValue = {
    enabled,
    setEnabled,
    toggle,
  };

  return (
    <SnowfallContext.Provider value={value}>
      {children}
    </SnowfallContext.Provider>
  );
}

/**
 * Hook to access and control the snowfall toggle state.
 *
 * If used outside of a SnowfallProvider, returns a default "enabled" state
 * that cannot be toggled (graceful fallback).
 *
 * @example
 * ```tsx
 * function SettingsPanel() {
 *   const { enabled, setEnabled, toggle } = useSnowfallToggle();
 *
 *   return (
 *     <Switch
 *       checked={enabled}
 *       onChange={toggle}
 *       label="Holiday Snow Effect"
 *     />
 *   );
 * }
 * ```
 */
export function useSnowfallToggle(): SnowfallContextValue {
  const context = useContext(SnowfallContext);

  // Graceful fallback if not in a provider
  if (!context) {
    return {
      enabled: true,
      setEnabled: () => {},
      toggle: () => {},
    };
  }

  return context;
}
